import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Link from '@mui/material/Link';

const fieldProps = { size: 'medium', fullWidth: true };

function BuildInventoryViewer() {
  const [inventory, setInventory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSoftwareType, setFilterSoftwareType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortAsc, setSortAsc] = useState(true);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [selectedTrackingRecord, setSelectedTrackingRecord] = useState(null);
  const [detailRecord, setDetailRecord] = useState(null);
  const [graphData, setGraphData] = useState([]);

  const [metrics, setMetrics] = useState({
    cirrusProOnline: 0,
    shippedThisMonth: 0,
    shippedLastMonth: 0,
    readyToBuild: 0
  });
  

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
  const { data, error } = await supabase.from('build_inventory').select('*');
  if (!error) {
    setInventory(data);

    // Count of Online devices per software_type
    const onlineDevices = data.filter(d => d.status === 'Online');
    const classicCount = onlineDevices.filter(d => d.software_type === 'Classic').length;
    const cirrusCount = onlineDevices.filter(d => d.software_type === 'CirrusPro').length;

    setGraphData([
      { name: 'Classic', count: classicCount },
      { name: 'CirrusPro', count: cirrusCount }
    ]);

    const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const cirrusProOnline = data.filter(
        d => d.software_type === 'CirrusPro' && d.status === 'Online'
      ).length;

      const shippedThisMonth = data.filter(d => {
        const shipDate = d.ship_date ? new Date(d.ship_date) : null;
        return d.status === 'Shipped' &&
          shipDate &&
          shipDate.getMonth() === currentMonth &&
          shipDate.getFullYear() === currentYear;
      }).length;

      const shippedLastMonth = data.filter(d => {
        const shipDate = d.ship_date ? new Date(d.ship_date) : null;
        const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
        return d.status === 'Shipped' &&
          shipDate &&
          shipDate.getMonth() === lastMonth &&
          shipDate.getFullYear() === lastMonthYear;
      }).length;

      const readyToBuild = data.filter(d => d.status === 'Not Built').length;

      setMetrics({
        cirrusProOnline,
        shippedThisMonth,
        shippedLastMonth,
        readyToBuild
      });

  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Online':
      return {
        background: '#e0f7e9',
        text: '#0b8e45',
        dot: '#0b8e45'
      };
    case 'Shipped':
      return {
        background: '#e6f4ff',
        text: '#0077cc',
        dot: '#0077cc'
      };
    case 'Not Built':
      return {
        background: '#fff4e5',
        text: '#d97706',
        dot: '#d97706'
      };
    default:
      return {
        background: '#f0f0f0',
        text: '#666',
        dot: '#999'
      };
  }
};

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterSoftwareType('');
    setFilterStatus('');
  };

  const filteredSortedInventory = [...inventory]
    .filter(item =>
      Object.values(item).some(value =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    )
    .filter(item => filterStatus ? item.status === filterStatus : true)
    .filter(item => filterSoftwareType ? item.software_type === filterSoftwareType : true)
    .sort((a, b) => {
      if (sortField) {
        const valA = a[sortField] ?? '';
        const valB = b[sortField] ?? '';
        if (sortField === 'ship_date') {
          return sortAsc
            ? new Date(valA || 0) - new Date(valB || 0)
            : new Date(valB || 0) - new Date(valA || 0);
        }
        return sortAsc
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      } else {
        const statusOrder = { 'Online': 0, 'Shipped': 1, 'Not Built': 2 };
        const softwareOrder = { 'CirrusPro': 0, 'Classic': 1 };

        const getWeight = (item) => {
          const statusWeight = statusOrder[item.status] ?? 99;
          const softwareWeight = softwareOrder[item.software_type] ?? 99;
          return statusWeight * 10 + softwareWeight;
        };

        const weightA = getWeight(a);
        const weightB = getWeight(b);

        // First, sort by weight
        if (weightA !== weightB) {
          return weightA - weightB;
        }

        // Then, if both are Shipped, sort by ship_date DESC
        if (a.status === 'Shipped' && b.status === 'Shipped') {
          const dateA = a.ship_date ? new Date(a.ship_date) : new Date(0);
          const dateB = b.ship_date ? new Date(b.ship_date) : new Date(0);
          return dateB - dateA; // latest date first
        }

        // Otherwise, keep original
        return 0;
      }
    });

  return (
    <Stack spacing={3}>
      <ToastContainer position="top-right" autoClose={3000} />
      <Typography variant="h5" component="h2" fontWeight={700}>
        Rain Bird build inventory
      </Typography>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Online devices
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={graphData}>
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip
                  formatter={(value) => [value, 'Built and Online']}
                  cursor={{ fill: 'transparent' }}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                />
                <Bar
                  dataKey="count"
                  name="Built and Online"
                  fill="#fc7b03"
                  isAnimationActive={false}
                  activeBar={(props) => (
                    <rect
                      x={props.x}
                      y={props.y}
                      width={props.width}
                      height={props.height}
                      fill="#fc7b03"
                    />
                  )}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Information
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2"><strong>CirrusPro devices online:</strong> {metrics.cirrusProOnline}</Typography>
              <Typography variant="body2"><strong>Devices shipped this month:</strong> {metrics.shippedThisMonth}</Typography>
              <Typography variant="body2"><strong>Devices shipped last month:</strong> {metrics.shippedLastMonth}</Typography>
              <Typography variant="body2"><strong>Devices in stock, ready to build:</strong> {metrics.readyToBuild}</Typography>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              {...fieldProps}
              label="Search"
              placeholder="Search…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              {...fieldProps}
              select
              label="Software type"
              id="softwareType"
              value={filterSoftwareType}
              onChange={(e) => setFilterSoftwareType(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="CirrusPro">CirrusPro</MenuItem>
              <MenuItem value="Classic">Classic</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              {...fieldProps}
              select
              label="Status"
              id="status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Online">Online</MenuItem>
              <MenuItem value="Shipped">Shipped</MenuItem>
              <MenuItem value="Not Built">Not Built</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Button variant="outlined" onClick={handleClearFilters}>
              Clear filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper
        sx={{
          p: 0,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: (theme) =>
              theme.palette.mode === 'light' ? 'rgba(15, 23, 42, 0.03)' : 'action.hover'
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Device inventory
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            {filteredSortedInventory.length} device{filteredSortedInventory.length === 1 ? '' : 's'}
            {filteredSortedInventory.length !== inventory.length ? ` (filtered from ${inventory.length})` : ''}
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {' · '}
              Row opens details
            </Box>
          </Typography>
        </Box>
        <TableContainer sx={{ maxWidth: '100%' }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 640,
              '& .MuiTableCell-root': {
                borderColor: 'divider'
              }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    py: 1.25,
                    borderBottomWidth: 2,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Serial #
                </TableCell>
                <TableCell
                  sortDirection={sortField === 'status' ? (sortAsc ? 'asc' : 'desc') : false}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    py: 1.25,
                    borderBottomWidth: 2,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <TableSortLabel
                    active={sortField === 'status'}
                    direction={sortField === 'status' ? (sortAsc ? 'asc' : 'desc') : 'asc'}
                    onClick={() => {
                      setSortField('status');
                      setSortAsc(sortField === 'status' ? !sortAsc : true);
                    }}
                    sx={{
                      '&.MuiTableSortLabel-root': { color: 'text.secondary' },
                      '&.MuiTableSortLabel-root.Mui-active': { color: 'text.primary' },
                      '& .MuiTableSortLabel-icon': { opacity: sortField === 'status' ? 1 : 0.35 }
                    }}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    py: 1.25,
                    borderBottomWidth: 2,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Customer
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    py: 1.25,
                    borderBottomWidth: 2,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Address
                </TableCell>
                <TableCell
                  sortDirection={sortField === 'ship_date' ? (sortAsc ? 'asc' : 'desc') : false}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    py: 1.25,
                    borderBottomWidth: 2,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <TableSortLabel
                    active={sortField === 'ship_date'}
                    direction={sortField === 'ship_date' ? (sortAsc ? 'asc' : 'desc') : 'asc'}
                    onClick={() => {
                      setSortField('ship_date');
                      setSortAsc(sortField === 'ship_date' ? !sortAsc : true);
                    }}
                    sx={{
                      '&.MuiTableSortLabel-root': { color: 'text.secondary' },
                      '&.MuiTableSortLabel-root.Mui-active': { color: 'text.primary' },
                      '& .MuiTableSortLabel-icon': { opacity: sortField === 'ship_date' ? 1 : 0.35 }
                    }}
                  >
                    Ship date
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSortedInventory.map((item) => {
                const statusColors = getStatusColor(item.status);
                return (
                  <TableRow
                    key={item.dell_service_tag}
                    hover
                    onClick={() => setDetailRecord(item)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      '&:nth-of-type(even)': {
                        bgcolor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(15, 23, 42, 0.02)'
                            : 'action.hover'
                      }
                    }}
                  >
                    <TableCell sx={{ py: 1, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {item.dell_service_tag}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Chip
                        size="small"
                        label={(item.status || 'Unknown').toUpperCase()}
                        sx={{
                          height: 26,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          letterSpacing: '0.02em',
                          bgcolor: statusColors.background,
                          color: statusColors.text,
                          border: 'none',
                          '& .MuiChip-label': { px: 1.25 }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, color: 'text.secondary', maxWidth: 160 }}>
                      {item.customer_name || '—'}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1,
                        color: item.customer_address ? 'secondary.main' : 'text.disabled',
                        cursor: item.customer_address ? 'pointer' : 'default',
                        maxWidth: 220,
                        '&:hover': item.customer_address
                          ? { textDecoration: 'underline', textUnderlineOffset: 2 }
                          : undefined
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.customer_address) {
                          const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.customer_address)}`;
                          window.open(url, '_blank');
                        }
                      }}
                    >
                      {item.customer_address || '—'}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1,
                        color: item.ship_date ? 'primary.main' : 'text.disabled',
                        cursor: item.ship_date ? 'pointer' : 'default',
                        fontWeight: item.ship_date ? 600 : 400,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                        '&:hover': item.ship_date
                          ? { textDecoration: 'underline', textUnderlineOffset: 2 }
                          : undefined
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTrackingRecord(item);
                        setShowTrackingModal(true);
                      }}
                    >
                      {item.ship_date ? item.ship_date.split('-').reverse().join('-') : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={Boolean(detailRecord)}
        onClose={() => setDetailRecord(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        aria-labelledby="viewer-device-detail-title"
      >
        {detailRecord && (
          <>
            <DialogTitle id="viewer-device-detail-title">Device · {detailRecord.dell_service_tag}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5} sx={{ pt: 0.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Software type
                  </Typography>
                  {detailRecord.software_type ? (
                    <Chip
                      label={detailRecord.software_type}
                      size="small"
                      variant="outlined"
                      sx={{ height: 28, fontWeight: 600, borderColor: 'divider' }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Windows license key
                  </Typography>
                  <Stack direction="row" alignItems="flex-start" spacing={1} flexWrap="wrap" useFlexGap>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: 'break-all', flex: '1 1 200px', fontFamily: 'ui-monospace, monospace' }}
                    >
                      {detailRecord.windows_license_key || '—'}
                    </Typography>
                    {detailRecord.windows_license_key ? (
                      <Button
                        size="small"
                        variant="outlined"
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(detailRecord.windows_license_key);
                          toast.success('License key copied to clipboard!');
                        }}
                      >
                        Copy
                      </Button>
                    ) : null}
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    size="small"
                    label={(detailRecord.status || 'Unknown').toUpperCase()}
                    sx={{
                      height: 28,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      letterSpacing: '0.02em',
                      bgcolor: getStatusColor(detailRecord.status).background,
                      color: getStatusColor(detailRecord.status).text,
                      border: 'none'
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Customer
                  </Typography>
                  <Typography variant="body2">{detailRecord.customer_name || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Address
                  </Typography>
                  {detailRecord.customer_address ? (
                    <Link
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(detailRecord.customer_address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      underline="hover"
                    >
                      {detailRecord.customer_address}
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Ship date
                  </Typography>
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {detailRecord.ship_date
                      ? detailRecord.ship_date.split('-').reverse().join('-')
                      : '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Tracking
                  </Typography>
                  <Typography variant="body2">{detailRecord.tracking_number || '—'}</Typography>
                  {detailRecord.tracking_url ? (
                    <Link
                      href={detailRecord.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      underline="hover"
                      sx={{ display: 'inline-block', mt: 0.5, wordBreak: 'break-all' }}
                    >
                      {detailRecord.tracking_url}
                    </Link>
                  ) : null}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button type="button" onClick={() => setDetailRecord(null)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={showTrackingModal && !!selectedTrackingRecord}
        onClose={() => setShowTrackingModal(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedTrackingRecord && (
          <>
            <DialogTitle sx={{ color: 'text.secondary' }}>
              Tracking info for {selectedTrackingRecord.dell_service_tag}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Tracking number</Typography>
                  <Typography variant="body2">{selectedTrackingRecord.tracking_number || 'None set'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Tracking URL</Typography>
                  {selectedTrackingRecord.tracking_url ? (
                    <Typography
                      component="a"
                      variant="body2"
                      href={selectedTrackingRecord.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ wordBreak: 'break-all' }}
                    >
                      {selectedTrackingRecord.tracking_url}
                    </Typography>
                  ) : (
                    <Typography variant="body2">None set</Typography>
                  )}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowTrackingModal(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Stack>
  );
}

export default BuildInventoryViewer;